/**
 * PublicShare — no-auth page rendering a public progress card.
 *
 * Uses direct fetch (no Authorization header) so it works in incognito.
 */
import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft, ShieldCheck } from "lucide-react";
import ShareCard from "@/components/ShareCard";
import { API } from "@/lib/api";

export default function PublicShare() {
    const { id } = useParams();
    const [state, setState] = useState({ loading: true, snapshot: null, error: null });
    const cardRef = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                const res = await fetch(`${API}/share/public/${id}`);
                if (res.status === 410) {
                    setState({ loading: false, snapshot: null, error: "Cette carte a été révoquée par son auteur." });
                    return;
                }
                if (!res.ok) {
                    setState({ loading: false, snapshot: null, error: "Carte introuvable." });
                    return;
                }
                const data = await res.json();
                setState({ loading: false, snapshot: data.snapshot || null, error: null });
            } catch {
                setState({ loading: false, snapshot: null, error: "Impossible de charger la carte." });
            }
        })();
    }, [id]);

    if (state.loading) {
        return (
            <div className="min-h-screen bg-paper flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-terracotta animate-spin" />
            </div>
        );
    }

    if (state.error) {
        return (
            <div className="min-h-screen bg-paper flex flex-col items-center justify-center p-6" data-testid="public-share-error">
                <p className="font-serif text-2xl">{state.error}</p>
                <Link to="/" className="mt-4 text-sm text-terracotta hover:underline">
                    Retour à Lexia School
                </Link>
            </div>
        );
    }

    const publicUrl = typeof window !== "undefined" ? window.location.href : null;

    return (
        <div className="min-h-screen bg-paper" data-testid="public-share-page">
            <header className="border-b border-lexiBorder bg-white">
                <div className="max-w-4xl mx-auto flex items-center justify-between px-4 sm:px-6 lg:px-8 py-4">
                    <Link to="/" className="font-serif text-lg" data-testid="public-share-brand">
                        Lexia School
                    </Link>
                    <Link to="/register" className="text-sm text-terracotta hover:underline">
                        Créer mon compte →
                    </Link>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
                <p className="text-xs tracking-[0.28em] uppercase text-inkSoft mb-3">Progression partagée</p>
                <h1 className="font-serif text-3xl md:text-4xl leading-tight" data-testid="public-share-title">
                    Un aperçu du parcours de {state.snapshot.first_name} sur Lexia School.
                </h1>
                <p className="mt-3 text-sm text-inkSoft max-w-2xl inline-flex items-start gap-2">
                    <ShieldCheck className="w-4 h-4 mt-0.5 text-emerald shrink-0" />
                    Carte publique en lecture seule — les informations sont figées au moment de sa création
                    et peuvent être révoquées à tout instant par son propriétaire.
                </p>

                <div className="mt-8">
                    <ShareCard ref={cardRef} snapshot={state.snapshot} publicUrl={publicUrl} />
                </div>

                <div className="mt-10 text-center">
                    <p className="text-sm text-inkSoft">Envie de faire pareil ?</p>
                    <Link
                        to="/register"
                        data-testid="public-share-cta"
                        className="inline-flex items-center gap-2 mt-3 bg-terracotta text-white hover:bg-terracottaDark rounded-full px-5 py-2.5 text-sm"
                    >
                        Commencer mon apprentissage
                    </Link>
                </div>
            </main>
        </div>
    );
}
